import { AnalyticsEventType, type ChatMessage, type SatisfactionForm } from "@do/shared-types";
import { useCallback, useEffect, useRef, useState } from "react";
import { type LastClickedButton } from "../../../../providers/chat-provider";

const getLastBotMessage = (messages: ChatMessage[]): ChatMessage | undefined => {
  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i];
    if (!message.user && !message.advisor) return message;
  }
  return undefined;
};

// do-bot exposes the cuid of the orchestrator response a bot message belongs to in its
// metadata (`metadata: [{ ..., response_cuid: "…" }]`). This is the only reliable source
// for response-based triggers: message blocks carry only block cuids, never their parent
// response cuid.
const getResponseCuidFromMessage = (message?: ChatMessage): string | null => {
  if (!message || !Array.isArray(message.metadata)) return null;
  for (const entry of message.metadata) {
    const responseCuid = entry?.["response_cuid"];
    if (typeof responseCuid === "string" && responseCuid.length > 0) return responseCuid;
  }
  return null;
};

const scheduleShow = (delaySeconds: number, show: () => void) => {
  const timer = window.setTimeout(() => show(), delaySeconds * 1000);
  return () => window.clearTimeout(timer);
};

type UseSatisfactionFormVisibilityParams = {
  satisfactionForm?: SatisfactionForm | null;
  messages: ChatMessage[];
  lastClickedButton?: LastClickedButton;
  isLoading?: boolean;
  // When true (e.g. crai-office preview), the form is always shown and triggers /
  // persistence are bypassed.
  forceVisible?: boolean;
  // Event types already recorded for the session (from analytics_events). When it
  // already contains `survey_displayed`, the form has been shown this session and
  // won't be shown again.
  recordedEventTypes?: AnalyticsEventType[];
  // Records the `survey_displayed` event once the form is shown, so it is persisted
  // server-side and not shown again for the session.
  onTrackEvent?: (event: {
    type: AnalyticsEventType;
    properties?: Record<string, unknown>;
  }) => void | Promise<void>;
  // Timestamp set by the host when a livechat conversation ends. Arms the
  // `livechat_end_of_conversation` trigger (if configured), and restarts the
  // `inactivity` countdown from the end of the livechat conversation.
  liveChatEndedAt?: number | null;
  // True while a livechat conversation is ongoing. Suppresses the `inactivity` trigger.
  isLiveChatActive?: boolean;
};

type UseSatisfactionFormVisibilityResult = {
  isVisible: boolean;
  dismiss: () => void;
};

/**
 * Decides whether the satisfaction form should be displayed, based on its triggers.
 *
 * Rules:
 * - The form is shown at most once per session, even after the user closes it. That
 *   fact is persisted server-side via a `survey_displayed` analytics event (see
 *   `recordedEventTypes` / `onTrackEvent`).
 * - The `inactivity` trigger only arms once the user has interacted with the bot,
 *   and is suppressed while the bot is loading, while a livechat conversation is active,
 *   or when the response currently shown is in the trigger's excluded list. Its
 *   countdown restarts when the livechat conversation ends.
 * - The `response` trigger fires when the latest bot response matches a configured
 *   response cuid.
 * - The `button_click` trigger fires when the user clicks a button matching a
 *   configured cuid or type.
 * - The `livechat_end_of_conversation` trigger fires when the host signals that a
 *   livechat conversation has ended (`liveChatEndedAt`).
 * - All triggers honor `params.delay` (seconds); `0` shows immediately.
 *
 * The response cuid is read from the latest bot message's metadata (`response_cuid`),
 * which do-bot includes for both NLU and button-driven responses.
 *
 * Any matching trigger makes the form visible (OR semantics).
 */
export function useSatisfactionFormVisibility({
  satisfactionForm,
  messages,
  lastClickedButton,
  isLoading,
  forceVisible = false,
  recordedEventTypes,
  onTrackEvent,
  liveChatEndedAt,
  isLiveChatActive = false,
}: UseSatisfactionFormVisibilityParams): UseSatisfactionFormVisibilityResult {
  const [isVisible, setIsVisible] = useState(false);
  const formCuid = satisfactionForm?.cuid ?? null;

  // Guard so a single click is only evaluated once (set when the click does not
  // match, or when the delayed show fires).
  const handledClickRef = useRef<LastClickedButton>(null);
  // Local guard so we don't re-trigger before the server-recorded event is reflected
  // back in `recordedEventTypes` (which is fetched asynchronously).
  const shownRef = useRef(false);

  // The cuid of the orchestrator response currently shown, read from the latest bot
  // message's metadata.
  const currentResponseCuid = getResponseCuidFromMessage(getLastBotMessage(messages));

  // True when the survey has already been shown this session: either recorded as a
  // `survey_displayed` analytics event, or just shown in this mount.
  const hasBeenShown =
    shownRef.current || (recordedEventTypes?.includes(AnalyticsEventType.SurveyDisplayed) ?? false);

  // When analytics tracking is enabled (`onTrackEvent` provided) but the already-recorded
  // events haven't loaded yet (`recordedEventTypes` still `undefined`), we can't know
  // whether the survey was already shown this session. Evaluating triggers now could both
  // re-display the survey and record a duplicate `survey_displayed` event, so we wait for
  // the fetch to settle (it resolves to `[]` or a populated list). Hosts without analytics
  // (no `onTrackEvent`) don't gate: triggers fire immediately, there is no persistence.
  const isAwaitingRecordedEvents = !!onTrackEvent && recordedEventTypes === undefined;

  // The satisfaction survey should be displayed only once per session.
  const show = useCallback(() => {
    if (!formCuid) return;
    if (shownRef.current) return;
    if (isAwaitingRecordedEvents) return;
    if (recordedEventTypes?.includes(AnalyticsEventType.SurveyDisplayed)) return;
    shownRef.current = true;
    void onTrackEvent?.({
      type: AnalyticsEventType.SurveyDisplayed,
      properties: { satisfaction_form_cuid: formCuid },
    });
    setIsVisible(true);
  }, [formCuid, isAwaitingRecordedEvents, recordedEventTypes, onTrackEvent]);

  // inactivity trigger
  useEffect(() => {
    if (forceVisible) return;
    if (!satisfactionForm || isVisible || isLoading) return;
    if (isAwaitingRecordedEvents || hasBeenShown) return;
    // Advisor replies can take longer than the delay: never show during a livechat.
    if (isLiveChatActive) return;

    const inactivity = satisfactionForm.triggers.find((trigger) => trigger.type === "inactivity");
    if (!inactivity) return;

    // Don't show for inactivity until the user has interacted with the bot.
    const userHasInteracted = messages.some((message) => message.user);
    if (!userHasInteracted) return;

    // Suppress if the response currently shown is excluded.
    const excluded = inactivity.params.excluded_response_cuids ?? [];
    if (currentResponseCuid && excluded.includes(currentResponseCuid)) return;

    // The timer resets on every message change (i.e. on any new activity), and when
    // the livechat conversation ends.
    return scheduleShow(inactivity.params.delay, show);
  }, [
    satisfactionForm,
    messages,
    isVisible,
    isLoading,
    isLiveChatActive,
    liveChatEndedAt,
    currentResponseCuid,
    forceVisible,
    isAwaitingRecordedEvents,
    hasBeenShown,
    show,
  ]);

  // response trigger
  useEffect(() => {
    if (forceVisible) return;
    if (!satisfactionForm || isVisible) return;
    if (isAwaitingRecordedEvents || hasBeenShown) return;

    const response = satisfactionForm.triggers.find((trigger) => trigger.type === "response");
    if (!response) return;

    if (!currentResponseCuid || !response.params.response_cuids.includes(currentResponseCuid)) {
      return;
    }

    return scheduleShow(response.params.delay, show);
  }, [
    satisfactionForm,
    currentResponseCuid,
    isVisible,
    forceVisible,
    isAwaitingRecordedEvents,
    hasBeenShown,
    show,
  ]);

  // button_click trigger
  useEffect(() => {
    if (forceVisible) return;
    if (!satisfactionForm || isVisible) return;
    if (isAwaitingRecordedEvents || hasBeenShown) return;
    if (!lastClickedButton || handledClickRef.current === lastClickedButton) return;

    const buttonClick = satisfactionForm.triggers.find(
      (trigger) => trigger.type === "button_click"
    );
    if (!buttonClick) {
      handledClickRef.current = lastClickedButton;
      return;
    }

    const { cuid, type } = lastClickedButton;
    const cuidMatch = !!cuid && buttonClick.params.button_cuids.includes(cuid);
    const typeMatch = !!type && buttonClick.params.button_types.includes(type);
    if (!cuidMatch && !typeMatch) {
      handledClickRef.current = lastClickedButton;
      return;
    }

    // Defer marking the click as handled until the timer fires so React Strict Mode
    // cleanup (which cancels the first timer) can re-schedule on the second mount.
    return scheduleShow(buttonClick.params.delay, () => {
      handledClickRef.current = lastClickedButton;
      show();
    });
  }, [
    satisfactionForm,
    lastClickedButton,
    isVisible,
    forceVisible,
    isAwaitingRecordedEvents,
    hasBeenShown,
    show,
  ]);

  // livechat_end_of_conversation trigger
  useEffect(() => {
    if (forceVisible) return;
    if (!satisfactionForm || isVisible) return;
    if (isAwaitingRecordedEvents || hasBeenShown) return;
    if (liveChatEndedAt == null) return;

    const livechatEnd = satisfactionForm.triggers.find(
      (trigger) => trigger.type === "livechat_end_of_conversation"
    );
    if (!livechatEnd) return;

    return scheduleShow(livechatEnd.params.delay, show);
  }, [
    satisfactionForm,
    liveChatEndedAt,
    isVisible,
    forceVisible,
    isAwaitingRecordedEvents,
    hasBeenShown,
    show,
  ]);

  const dismiss = useCallback(() => setIsVisible(false), []);

  return { isVisible: forceVisible || isVisible, dismiss };
}
